/* eslint-disable no-alert */
import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field, Form } from "formik";
import _omit from "lodash/omit";
import _filter from "lodash/filter";
import _isEqual from "lodash/isEqual";
import _sortBy from "lodash/sortBy";
import * as Yup from "yup";
import ValidationError from "components/lib/Shared/ValidationError";
import {
  addNominalShareAsync,
  updateNominalShareAsync,
} from "state/slices/tables/nominalSharesTable";
import {
  addShareAllotmentAsync,
  getShareAllotmentsAsync,
  updateShareAllotmentAsync,
} from "state/slices/shareCapitalAllotment";
import {
  addShareAuthorizationAsync,
  getShareAuthorizationsAsync,
  updateShareAuthorizationAsync,
} from "state/slices/shareCapitalAuthorization";
import { submitAuthorizationsAsync } from "state/slices/shareCapitalAuthorization";
import { closeModal } from "state/slices/modals";
import schemaGenerator from "./validationSchema";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { useToast } from "hooks/useToast";
import { Constants } from "config/constants";
import { ExclamationIcon } from "@heroicons/react/outline";
import ChangesContext from "context/ChangesContext";

const initialNominalOptions = [
  { value: Constants.ORDINARY_SHARES, label: Constants.ORDINARY_SHARES },
  { value: Constants.PREFERENCE_SHARES, label: Constants.PREFERENCE_SHARES },
  { value: Constants.OTHER_SHARES, label: Constants.OTHER_SHARES },
];

const preferenceRights = [
  { label: "Cumulative", order: 1, correlativeRight: "Non-Cumulative" },
  { label: "Redeemable", order: 3, correlativeRight: "Non-Redeemable" },
  { label: "Participating", order: 5, correlativeRight: "Non-Participating" },
  { label: "Convertible", order: 7, correlativeRight: "Non-Convertible" },
  { label: "Non-Cumulative", order: 2, correlativeRight: "Cumulative" },
  { label: "Non-Redeemable", order: 4, correlativeRight: "Redeemable" },
  { label: "Non-Participating", order: 6, correlativeRight: "Participating" },
  { label: "Non-Convertible", order: 8, correlativeRight: "Convertible" },
];

const ordinaryRights = [
  {
    label: "Redeemable",
    order: 1,
    correlativeRight: "Non-Redeemable",
    disabled: true,
  },
  {
    label: "Non-Redeemable",
    order: 2,
    correlativeRight: "Redeemable",
    disabled: true,
  },
];

const otherRights = [
  { label: "Redeemable", order: 1, correlativeRight: "Non-Redeemable" },
  { label: "Non-Redeemable", order: 2, correlativeRight: "Redeemable" },
];

function useNominalShareSelectionEffects(
  values,
  setFieldValue,
  prefixes,
  setPrefixes,
  currentRow
) {
  useEffect(() => {
    if (values.sharesCategory === Constants.ORDINARY_SHARES) {
      setFieldValue("hasVotingRights", "Yes");
    } else {
      setFieldValue("hasVotingRights", values.hasVotingRights || "");
    }
    if (
      values.sharesCategory === Constants.OTHER_SHARES ||
      [values?.sharesCategory, values?.baseSharesCategory].includes(
        Constants.PREFERENCE_SHARES
      )
    ) {
      setFieldValue("showCustomSharesCategory", true);
    } else {
      setFieldValue("showCustomSharesCategory", false);
    }
  }, [values.sharesCategory, setFieldValue]);

  useEffect(() => {
    if (values.hasVotingRights === "No") {
      setFieldValue("votesPerShare", 0);
    } else {
      setFieldValue("votesPerShare", currentRow?.votesPerShare || 1);
    }
  }, [values.hasVotingRights, currentRow?.votesPerShare, setFieldValue]);

  useEffect(() => {
    setFieldValue("rights", prefixes);
  }, [prefixes, setFieldValue]);

  useEffect(() => {
    if (values.sharesCategory === Constants.ORDINARY_SHARES) {
      setPrefixes([
        {
          label: "Non-Redeemable",
          order: 2,
          correlativeRight: "Redeemable",
          disabled: true,
        },
      ]);
    } else {
      setPrefixes([]);
    }
  }, [values.sharesCategory, setPrefixes]);
}

function NominalShareFormContent({
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  prefixes,
  setPrefixes,
  currentRow,
  mode,
  isInChangesModule,
  isInManualUpdate,
  changeType,
  isProposed,
  takenShares,
  shares,
  handleCancel,
  Constants,
  preferenceRights,
  ordinaryRights,
  otherRights,
  ExclamationIcon,
  ValidationError,
  Field,
  Form,
  SpinnerIcon,
  setShowAddMoreSharesBtn,
  handlePrefixChange,
  ...props
}) {
  useNominalShareSelectionEffects(
    values,
    setFieldValue,
    prefixes,
    setPrefixes,
    currentRow
  );

  const isFieldDisabled = () => {
    // Force read-only for proposed authorization edits: only sharesNumber remains editable
    if (isProposed && changeType === "authorization" && mode?.editing) {
      return true;
    }
    return (
      (isInChangesModule &&
        mode?.editing &&
        (!isProposed || currentRow?.isCreatedFromExistingNominalShare)) ||
      (isInManualUpdate &&
        mode?.editing &&
        (!isProposed || currentRow?.isCreatedFromExistingNominalShare))
    );
  };

  const getFieldStyling = () => {
    // Keep sharesNumber editable during proposed authorization edits
    if (
      isProposed &&
      (changeType === "authorization" ||
        changeType === "privateWithMoreThanOneClass") &&
      mode?.editing
    ) {
      return "bg-gray-200";
    }
    return isFieldDisabled() ? "bg-gray-200" : "";
  };

  let rights = [];
  if (
    [values.sharesCategory, values?.baseSharesCategory].includes(
      Constants.PREFERENCE_SHARES
    )
  ) {
    rights = preferenceRights;
  } else if (values.sharesCategory === Constants.ORDINARY_SHARES) {
    rights = ordinaryRights;
  } else if (values.sharesCategory === Constants.OTHER_SHARES) {
    rights = otherRights;
  }

  const titlePrefix = _sortBy(values.rights, ["order"])
    .map(({ label }) => label)
    .join(" ");
  let combinationAlreadyExists = false;
  shares.forEach(({ hasVotingRights, rights }) => {
    const tempTitlePrefix = _sortBy(rights, ["order"])
      .map(({ label }) => label)
      .join(" ");
    if (
      hasVotingRights === values.hasVotingRights &&
      !!titlePrefix &&
      titlePrefix === tempTitlePrefix
    ) {
      combinationAlreadyExists = true;
    }
  });

  return (
    <Form>
      <div className="px-4 py-6 space-y-6 sm:p-6">
        <div>
          <label className="text-base font-medium text-gray-900">
            Share category type
          </label>
          <fieldset className="mt-4">
            <legend className="sr-only">Share category type</legend>
            <div className="space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
              {initialNominalOptions.map((nominalOption) => (
                <div key={nominalOption.value} className="flex items-center">
                  <Field
                    id={nominalOption.value}
                    name="sharesCategory"
                    type="radio"
                    disabled={isFieldDisabled()}
                    value={nominalOption.value}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <label
                    htmlFor={nominalOption.value}
                    className="block ml-3 text-sm font-medium text-gray-700"
                  >
                    {nominalOption.label}
                  </label>
                </div>
              ))}
            </div>
          </fieldset>
          <ValidationError
            errors={errors}
            touched={touched}
            name="sharesCategory"
          />
        </div>
        {(values.sharesCategory === Constants.OTHER_SHARES ||
          values.showCustomSharesCategory) && (
          <div>
            <label
              htmlFor="customSharesCategory"
              className="block text-sm font-medium text-gray-700"
            >
              Custom Shares Category
            </label>
            <Field
              name="customSharesCategory"
              disabled={isFieldDisabled()}
              placeholder="Enter a unique shares name. Eg Ordinary A"
              className={`${getFieldStyling()} block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm capitalized focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
            />
            <ValidationError
              errors={errors}
              touched={touched}
              name="customSharesCategory"
            />
          </div>
        )}
        <hr />
        <div>
          <label className="text-base font-medium text-gray-900">
            Do the shares have voting rights?
          </label>
          <fieldset className="mt-4">
            <legend className="sr-only">
              Do the shares have voting rights?
            </legend>
            <div className="space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
              {["Yes", "No"].map((votingOption) => (
                <div key={votingOption} className="flex items-center">
                  <Field
                    type="radio"
                    name="hasVotingRights"
                    value={votingOption}
                    id={votingOption}
                    className={`${
                      values.sharesCategory === Constants.ORDINARY_SHARES
                        ? "border-gray-200"
                        : "border-gray-300"
                    } w-4 h-4 text-indigo-600 focus:ring-indigo-500`}
                    disabled={
                      isFieldDisabled() ||
                      values.sharesCategory === Constants.ORDINARY_SHARES
                    }
                  />
                  <label
                    htmlFor={votingOption}
                    className={`${
                      values.sharesCategory === Constants.ORDINARY_SHARES &&
                      votingOption === "No"
                        ? "text-gray-300"
                        : "text-gray-700"
                    } block ml-3 text-sm font-medium`}
                  >
                    {votingOption}
                  </label>
                </div>
              ))}
            </div>
          </fieldset>
          <ValidationError
            errors={errors}
            touched={touched}
            name="hasVotingRights"
          />
        </div>
        <hr />
        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6 sm:col-span-2">
            <label
              htmlFor="sharesNumber"
              className="block text-sm font-medium text-gray-700"
            >
              {mode?.editing ? "Update number of shares" : "Number of shares"}
            </label>
            <Field
              id="sharesNumber"
              name="sharesNumber"
              type="number"
              onWheel={(e) => e.target.blur()}
              value={values.sharesNumber}
              placeholder={
                mode?.editing
                  ? "Update number of shares. Eg 100"
                  : "Add number of shares. Eg 100"
              }
              className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm capitalized focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <ValidationError
              errors={errors}
              touched={touched}
              name="sharesNumber"
            />
          </div>
          <div className="col-span-6 sm:col-span-2">
            <label
              htmlFor="sharesValue"
              className="block text-sm font-medium text-gray-700"
            >
              Value of shares
            </label>
            <Field
              id="sharesValue"
              name="sharesValue"
              type="number"
              value={
                isInChangesModule || isInManualUpdate
                  ? currentRow?.sharesValue || values.sharesValue || ""
                  : values.sharesValue
              }
              onWheel={(e) => e.target.blur()}
              placeholder={
                (isInChangesModule || isInManualUpdate) && mode?.editing
                  ? ""
                  : "Add value of shares. Eg 100"
              }
              className={`${getFieldStyling()} block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm capitalized focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
              disabled={isFieldDisabled()}
              min={1}
            />
            <ValidationError
              errors={errors}
              touched={touched}
              name="sharesValue"
            />
          </div>
          <div className="col-span-6 sm:col-span-2">
            <label
              htmlFor="votesPerShare"
              className="block text-sm font-medium text-gray-700"
            >
              Votes Per Share
            </label>
            <Field
              id="votesPerShare"
              name="votesPerShare"
              type="number"
              value={
                isInChangesModule || isInManualUpdate
                  ? currentRow?.votesPerShare || values.votesPerShare || 1
                  : values.votesPerShare
              }
              disabled={values.hasVotingRights === "No" || isFieldDisabled()}
              onWheel={(e) => e.target.blur()}
              placeholder={
                isInChangesModule || isInManualUpdate ? "" : "Not less than 1"
              }
              className={`${
                values.hasVotingRights === "No" || isFieldDisabled()
                  ? "bg-gray-200"
                  : ""
              } block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm capitalized focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
              min={1}
            />
            <ValidationError
              errors={errors}
              touched={touched}
              name="votesPerShare"
            />
          </div>
        </div>
        {rights.length > 0 && (
          <>
            <p className="pt-4 font-semibold border-t">
              Please select the rights affiliated to your class of shares
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {rights.map((prefix) => {
                const disabled =
                  prefixes.find(
                    ({ label }) => label === prefix.correlativeRight
                  ) || prefix.disabled;
                return (
                  <div
                    className={`relative flex items-start mb-2 mr-4`}
                    key={prefix.label}
                  >
                    <div className="flex items-center h-5">
                      <input
                        id={prefix.label}
                        aria-describedby="preference-share-name-prefix"
                        name={prefix.label}
                        type="checkbox"
                        checked={prefixes.some((p) => p.label === prefix.label)}
                        onChange={({ target: { name, checked } }) => {
                          if (!disabled) {
                            handlePrefixChange(name, checked, prefix);
                          }
                        }}
                        disabled={disabled || isFieldDisabled()}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:cursor-not-allowed"
                      />
                    </div>
                    <div className="ml-2 text-sm">
                      <label
                        htmlFor={prefix.label}
                        className={`font-medium ${
                          disabled || isFieldDisabled()
                            ? "cursor-not-allowed text-gray-300"
                            : "text-gray-700"
                        }`}
                      >
                        {prefix.label}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        <ValidationError errors={errors} touched={touched} name="rights" />
        {combinationAlreadyExists &&
          values.sharesCategory === Constants.OTHER_SHARES && (
            <div className="rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationIcon
                    className="h-5 w-5 text-yellow-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Attention needed
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>This combination of rights already exists</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        <div className="flex justify-end mt-2 space-x-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex justify-center px-4 py-2 ml-auto text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
            style={{ alignSelf: "center" }}
          >
            {isSubmitting ? (
              <>
                <SpinnerIcon />
                Submitting...
              </>
            ) : mode?.editing ? (
              "Submit"
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </Form>
  );
}

const NominalShareSelection = (props) => {
  const [nominalOptions, setNominalOptions] = useState(initialNominalOptions);
  const dispatch = useDispatch();
  const { toast } = useToast(5000);
  const company = useSelector(
    (state) =>
      state.companySlice?.company || state.companyDashboardSlice?.company
  );
  let { shares: reduxShares } = useSelector((state) => state.sharesTableSlice);
  const [prefixes, setPrefixes] = useState([]);
  const { isInChangesModule } = useContext(ChangesContext);
  const isInManualUpdate = props?.isInManualUpdate;

  const {
    mode,
    takenShares,
    setShowAddMoreSharesBtn,
    sharesCategory = "",
    nominalShares,
    closeSubRow,
    isProposed,
    changeType,
    createdTransactionsIds,
    setCreatedTransactionsIds,
  } = props;

  useEffect(() => {
    if (sharesCategory) {
      setNominalOptions(
        initialNominalOptions.filter(
          (option) => option.value === sharesCategory
        )
      );
    }
  }, [sharesCategory]);

  // Prefer proposed/explicit data passed in via props when available
  let shares = isProposed
    ? props.nominalShares || []
    : isInChangesModule
    ? props.nominalShares || []
    : reduxShares || [];

  let currentRow = {};
  if (mode?.id) {
    currentRow = shares.find((share) => share._id === mode.id) || {};
  }

  useEffect(() => {
    if (currentRow?.rights) {
      setPrefixes(currentRow?.rights ?? []);
    }
  }, [currentRow]);

  const handlePrefixChange = (name, checked, prefix) => {
    if (checked) setPrefixes((prevValue) => [...prevValue, prefix]);
    else
      setPrefixes((prevValue) =>
        prevValue.filter((prefix) => prefix.label !== name)
      );
  };

  const handleCancel = () => {
    // If isProposed or if in changes/manual mode, close the subrow first
    if ((isProposed || isInChangesModule || isInManualUpdate) && closeSubRow) {
      closeSubRow();
    } else {
      dispatch(closeModal("main_shares_modal_edit"));
      // Only call setShowAddMoreSharesBtn if it's available (not in proposed mode)
      if (
        setShowAddMoreSharesBtn &&
        typeof setShowAddMoreSharesBtn === "function"
      ) {
        setShowAddMoreSharesBtn(true);
      }
    }
  };

  const handleCompanyInChangesSubmit = async (values) => {
    const companyId = company?._id;
    if (!companyId) {
      toast("error", "Something went wrong. Company ID not found.");
      return;
    }

    let payload = _omit(values, [
      "customSharesCategory",
      "showCustomSharesCategory",
      "isProposed",
    ]);

    // Map custom share category
    if (
      values?.customSharesCategory &&
      [Constants.OTHER_SHARES, Constants.PREFERENCE_SHARES].includes(
        values.sharesCategory
      )
    ) {
      payload = {
        ...payload,
        sharesCategory: values.customSharesCategory,
        baseSharesCategory: values.sharesCategory,
      };
    }

    // Derived fields
    payload.nominalValue = payload.sharesNumber * payload.sharesValue;
    payload.votingRights = payload.sharesNumber * payload.votesPerShare;
    payload.titlePrefix = _sortBy(values.rights, ["order"])
      .map(({ label }) => label)
      .join(" ");

    // Set entity/template type in non-special editing scenarios
    if (
      !(
        (isInChangesModule || isInManualUpdate) &&
        mode?.editing &&
        (!isProposed || currentRow?.isCreatedFromExistingNominalShare)
      )
    ) {
      payload.entityType = company.type;
      payload.templateType = [
        Constants.ORDINARY_SHARES,
        Constants.PREFERENCE_SHARES,
      ].includes(payload.sharesCategory)
        ? payload.sharesCategory
        : Constants.OTHER_SHARES;
    }

    payload.existing = (isInChangesModule || isInManualUpdate) && mode?.editing;

    if (!values.isProposed) {
      payload.changeType = changeType;
    }

    try {
      // ALWAYS use authorization flow when in changes module or manual update mode
      let submissionPayload = {
        ...payload,
        companyId,
        // Set appropriate approval status based on change type
        isApproved:
          changeType === "privateWithMoreThanOneClass" ? false : false,
      };

      // For proposed updates, we need the ID from the current row
      if (values.isProposed && mode?.editing) {
        if (mode.id) {
          submissionPayload.id = mode.id;
        } else if (currentRow._id) {
          submissionPayload.id = currentRow._id;
        }
      }

      // Ensure we always use updateShareAuthorizationAsync when isProposed === true
      // But check if we have required ID for update operations
      let action;
      if (values.isProposed) {
        if (!submissionPayload.id) {
          toast("error", "Unable to update authorization: Missing ID");
          return;
        }
        action = updateShareAuthorizationAsync(submissionPayload);
      } else {
        action = addShareAuthorizationAsync(submissionPayload);
      }

      const result = await dispatch(action);

      const ok =
        (values.isProposed &&
          updateShareAuthorizationAsync.fulfilled.match(result)) ||
        (!values.isProposed &&
          addShareAuthorizationAsync.fulfilled.match(result));

      if (ok) {
        const response = result.payload;
        const message =
          response?.data?.message ||
          response?.message ||
          "Authorization created successfully";

        // Create the related transaction immediately after successful authorization creation
        // Skip transaction creation for proposed updates to avoid conflicts
        if (!values.isProposed) {
          try {
            // Infer processType and changeType for transaction submission
            let processType = "authorization";
            let txChangeType = "AUTHORIZATION";
            let isApproved = false;
            if (changeType === "privateWithMoreThanOneClass") {
              processType = "privateWithMoreThanOneClass";
              txChangeType = "AUTHORIZATION-AND-ALLOTMENT";
              // Keep consistent with existing flow: mark as approved to proceed to allotment
              isApproved = true;
            }

            const txAction = await dispatch(
              submitAuthorizationsAsync({
                companyId,
                transactionType: Constants.TRANSACTION_TYPES.MANUAL,
                changeType: txChangeType,
                processType,
                isApproved,
                // Dates are not provided at this point; they'll be edited later if needed
                dateFields: { dateOfAuthorization: null, dateOfExpiry: null },
              })
            );

            if (submitAuthorizationsAsync.fulfilled.match(txAction)) {
              const trxId = txAction?.payload?.data?.transaction?._id;
              const createdAuthId =
                response?.data?.authorization?._id || response?.data?._id;

              if (trxId && setCreatedTransactionsIds) {
                setCreatedTransactionsIds((prev) =>
                  prev.includes(trxId) ? prev : [...prev, trxId]
                );
              }

              if (
                trxId &&
                createdAuthId &&
                !values.isProposed &&
                !mode?.editing
              ) {
                // Persist the link back onto the authorization for easier deletion later
                await dispatch(
                  updateShareAuthorizationAsync({
                    id: createdAuthId,
                    data: { trxId },
                  })
                );
              }
            } else {
              // Non-fatal: transaction creation failed; allow user to proceed manually
              // Optionally toast a subtle info
            }
          } catch (e) {
            // Swallow errors here to not block the primary success path
          }
        }

        // Refresh authorizations after successful submission
        await dispatch(getShareAuthorizationsAsync(companyId));
        handleCancel();
        toast("success", message);
      } else {
        handleCancel();
        const errorMessage =
          typeof result?.payload === "string"
            ? result.payload
            : (result?.payload && result.payload.messageText) ||
              "Failed to create authorization. Please try again.";
        toast("error", errorMessage);
      }
    } catch (error) {
      handleCancel();
      const errorMessage =
        error?.message ||
        error?.messageText ||
        "An unexpected error occurred while creating authorization. Please try again.";
      toast("error", errorMessage);
    }
  };

  const handleCompanyInDraftSubmit = async (values) => {
    if (!company?._id) {
      toast(
        "error",
        "Something went wrong please reload the page and try again."
      );
      return;
    }

    let payload = _omit(values, [
      "customSharesCategory",
      "showCustomSharesCategory",
      "isProposed",
      "dateOfOffer",
    ]);

    if (
      values?.customSharesCategory &&
      [Constants.OTHER_SHARES, Constants.PREFERENCE_SHARES].includes(
        values.sharesCategory
      )
    ) {
      payload = {
        ...payload,
        sharesCategory: values.customSharesCategory,
        baseSharesCategory: values.sharesCategory,
      };
    }

    payload.nominalValue = payload.sharesNumber * payload.sharesValue;
    payload.votingRights = payload.sharesNumber * payload.votesPerShare;

    if (mode?.editing) {
      if (!_isEqual(payload, currentRow)) {
        // Only call updateNominalShareAsync if we have a valid mode.id
        if (mode.id) {
          await dispatch(
            updateNominalShareAsync({ id: mode.id, data: payload })
          );
        }
      }
      dispatch(closeModal("main_shares_modal_edit"));
    } else {
      payload.entityType = company.type;
      payload.templateType = [
        Constants.ORDINARY_SHARES,
        Constants.PREFERENCE_SHARES,
      ].includes(values.sharesCategory)
        ? values.sharesCategory
        : Constants.OTHER_SHARES;
      await dispatch(
        addNominalShareAsync({ companyId: company._id, data: payload })
      );
      // Only call setShowAddMoreSharesBtn if it's available
      if (
        setShowAddMoreSharesBtn &&
        typeof setShowAddMoreSharesBtn === "function"
      ) {
        setShowAddMoreSharesBtn(true);
      }
    }
  };

  const handleSubmit = (values) => {
    // Always use changes submit when isProposed is true
    if (isProposed || isInChangesModule || isInManualUpdate) {
      handleCompanyInChangesSubmit(values);
    } else {
      // Only use normal share submission in draft mode
      handleCompanyInDraftSubmit(values);
    }
  };

  return (
    <Formik
      initialValues={
        mode?.editing
          ? {
              ...currentRow,
              customSharesCategory: currentRow.sharesCategory,
              sharesCategory:
                currentRow?.baseSharesCategory ?? currentRow.sharesCategory,
              sharesNumber:
                (isInChangesModule || isInManualUpdate) && !isProposed
                  ? ""
                  : (isInChangesModule || isInManualUpdate) && isProposed
                  ? currentRow.sharesNumber
                  : currentRow.sharesNumber || "",
              isProposed: isProposed,
              ...((changeType === "privateWithOneClass" ||
                changeType === "authorization" ||
                changeType === "privateWithMoreThanOneClass") &&
                !isProposed && {
                  dateOfOffer: currentRow?.dateOfOffer || null,
                }),
            }
          : {
              sharesCategory:
                isInChangesModule || isInManualUpdate
                  ? props.sharesCategory
                  : "",
              sharesNumber: "",
              sharesValue:
                isInChangesModule || isInManualUpdate
                  ? currentRow?.sharesValue || ""
                  : "",
              customSharesCategory: "",
              showCustomSharesCategory: false,
              hasVotingRights: "",
              votesPerShare: 1,
              isProposed: isProposed,
              ...((changeType === "privateWithOneClass" ||
                changeType === "authorization" ||
                changeType === "privateWithMoreThanOneClass") &&
                !isProposed && {
                  dateOfOffer: null,
                }),
            }
      }
      enableReinitialize
      validationSchema={schemaGenerator({
        takenSharesCategories: _filter(
          Array.isArray(shares)
            ? shares.map(({ sharesCategory }) =>
                props?.addDifferentCategory ? "" : sharesCategory
              )
            : [],
          (cat) => cat !== currentRow?.sharesCategory
        ),
        minSharesNumber: takenShares[currentRow?.sharesCategory] ?? 1,
        shares: Array.isArray(shares)
          ? shares.filter((share) => share._id !== currentRow?._id)
          : [],
        ...((changeType === "privateWithOneClass" ||
          changeType === "authorization" ||
          changeType === "privateWithMoreThanOneClass") &&
          !isProposed && {
            dateOfOffer: Yup.date()
              .required("Date of offer is required")
              .nullable(),
          }),
      })}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <NominalShareFormContent
          {...formikProps}
          prefixes={prefixes}
          setPrefixes={setPrefixes}
          currentRow={currentRow}
          mode={mode}
          isInChangesModule={isInChangesModule}
          isInManualUpdate={isInManualUpdate}
          changeType={changeType}
          isProposed={isProposed}
          takenShares={takenShares}
          shares={shares}
          handleCancel={handleCancel}
          handlePrefixChange={handlePrefixChange}
          Constants={Constants}
          preferenceRights={preferenceRights}
          ordinaryRights={ordinaryRights}
          otherRights={otherRights}
          ExclamationIcon={ExclamationIcon}
          ValidationError={ValidationError}
          Field={Field}
          Form={Form}
          SpinnerIcon={SpinnerIcon}
          setShowAddMoreSharesBtn={setShowAddMoreSharesBtn}
          {...props}
        />
      )}
    </Formik>
  );
};

export default NominalShareSelection;
